import type { IconType } from "@src/appV2/redesign/system/ui-components";
import { useMemo } from "react";

import { ReviewQuestionType } from "../../redesign/Workplace/Review/types";
import {
  type ConsolidatedQuestionItem,
  DEFAULT_QUESTION_TAG_ICON,
  LEAVE_REVIEW_ENTRYPOINT,
  type LeaveReviewEntrypoint,
  type NumberQuestionConfig,
  type PostShiftReviewStep,
  QUESTION_TAG_ICON_MAP,
  type QuestionMappings,
  QuestionTag,
} from "../types";
import { useIsWorkplaceReviewUXUpdateEnabled } from "./useIsWorkplaceReviewUXUpdateEnabled";

const RENDERABLE_QUESTION_TAGS = new Set<QuestionTag>([
  QuestionTag.SOFTWARE,
  QuestionTag.SUPPLIES,
  QuestionTag.PATIENTS_PER_NURSE,
  QuestionTag.PARKING,
]);

interface StepConfig {
  type: PostShiftReviewStep;
  required: boolean;
  questionId?: string;
  options?: Array<{ id: string; displayText: string }>;
  questionText?: string;
  questionType?: ReviewQuestionType;
  numberConfig?: NumberQuestionConfig;
  consolidatedQuestions?: ConsolidatedQuestionItem[];
}

export interface UsePostShiftReviewStepsProps {
  questionMappings?: QuestionMappings;
  isError?: boolean;
  hasComparisonPair?: boolean;
  canSubmitEmploymentVerification?: boolean;
  hasPeerComments?: boolean;
  source?: LeaveReviewEntrypoint;
}

const getQuestionTextForTag = (tag: QuestionTag): string => {
  // FIXME: Automatically disabled during ESLint config update
  switch (tag) {
    case QuestionTag.SOFTWARE: {
      return "What software was used for documentation?";
    }

    case QuestionTag.SUPPLIES: {
      return "Did you have sufficient supplies during the shift?";
    }

    case QuestionTag.PATIENTS_PER_NURSE: {
      return "How many residents were you responsible for during this shift?";
    }

    case QuestionTag.PARKING: {
      return "Tell us about parking at this location";
    }

    default: {
      return "";
    }
  }
};

function getIconTypeForTag(tag: QuestionTag): IconType {
  return (
    (
      QUESTION_TAG_ICON_MAP as Partial<
        Record<QuestionTag, (typeof QUESTION_TAG_ICON_MAP)[keyof typeof QUESTION_TAG_ICON_MAP]>
      >
    )[tag] ?? DEFAULT_QUESTION_TAG_ICON
  );
}

function buildConsolidatedQuestions(
  questionMappings: QuestionMappings
): ConsolidatedQuestionItem[] {
  const orderedTags = questionMappings.orderedTags ?? [];
  const consolidatedQuestions: ConsolidatedQuestionItem[] = [];

  for (const tag of orderedTags) {
    // Check for rating question
    const ratingQuestion = questionMappings.ratingQuestions.find((rq) => rq.tag === tag);
    if (ratingQuestion) {
      consolidatedQuestions.push({
        tag,
        questionId: ratingQuestion.questionId,
        questionText: ratingQuestion.questionText,
        questionType: ReviewQuestionType.RATING,
        iconType: getIconTypeForTag(tag),
      });
      continue;
    }

    // Check for non-rating question (NUMBER type like PATIENTS_PER_NURSE)
    if (RENDERABLE_QUESTION_TAGS.has(tag)) {
      const questionId = questionMappings.tagToQuestionId[tag];
      if (questionId) {
        const questionType = questionMappings.questionTypes[questionId];
        if (questionType === ReviewQuestionType.NUMBER) {
          consolidatedQuestions.push({
            tag,
            questionId,
            questionText: questionMappings.tagToQuestionText[tag] ?? getQuestionTextForTag(tag),
            questionType: ReviewQuestionType.NUMBER,
            numberConfig: questionMappings.questionNumberConfig[questionId],
            iconType: getIconTypeForTag(tag),
          });
        }
      }
    }
  }

  return consolidatedQuestions;
}

export function usePostShiftReviewSteps(props: UsePostShiftReviewStepsProps) {
  const {
    questionMappings,
    isError,
    hasComparisonPair = false,
    canSubmitEmploymentVerification = false,
    hasPeerComments = false,
    source,
  } = props;
  const { isEnabled: isWriteReviewUiUpdateEnabled } = useIsWorkplaceReviewUXUpdateEnabled();

  return useMemo(() => {
    const questionSteps: StepConfig[] = [];
    let writeReviewStep: StepConfig | undefined;

    if (!isError && questionMappings) {
      if (questionMappings.commentQuestionId) {
        writeReviewStep = { type: "write-review", required: true };
      }

      const orderedTags = questionMappings.orderedTags ?? [
        QuestionTag.SOFTWARE,
        QuestionTag.SUPPLIES,
        QuestionTag.PATIENTS_PER_NURSE,
        QuestionTag.PARKING,
        QuestionTag.CLEANLINESS,
        QuestionTag.SUPPLY_RATING,
        QuestionTag.CULTURE,
        QuestionTag.PATIENT_CARE,
      ];

      // Build individual question steps (used for old flow when flag is OFF)
      for (const tag of orderedTags) {
        if (RENDERABLE_QUESTION_TAGS.has(tag)) {
          const questionId = questionMappings.tagToQuestionId[tag];
          if (questionId) {
            const options = questionMappings.questionOptions[questionId];
            const numberConfig = questionMappings.questionNumberConfig[questionId];
            const questionType = questionMappings.questionTypes[questionId];

            questionSteps.push({
              type: tag as PostShiftReviewStep,
              required: false,
              questionId,
              options,
              questionText: getQuestionTextForTag(tag),
              questionType,
              numberConfig,
            });
          }
        }
      }
    }

    if (isWriteReviewUiUpdateEnabled) {
      // New flow with comparison: compare-workplaces → peer-reviews → category-ratings → write-review → anonymity → end
      // New flow without comparison: peer-reviews → category-ratings → write-review → anonymity → end
      const consolidatedQuestions = questionMappings
        ? buildConsolidatedQuestions(questionMappings)
        : [];

      const baseSteps: StepConfig[] = [];
      if (hasComparisonPair && source !== LEAVE_REVIEW_ENTRYPOINT.WORKPLACE_PROFILE) {
        baseSteps.push({ type: "compare-workplaces", required: false });
      }
      if (hasPeerComments && source !== LEAVE_REVIEW_ENTRYPOINT.WORKPLACE_PROFILE) {
        baseSteps.push({ type: "peer-reviews", required: false });
      }

      const contentSteps: StepConfig[] = [];
      if (consolidatedQuestions.length > 0) {
        contentSteps.push({
          type: "rate-workplace-questions",
          required: false,
          consolidatedQuestions,
        });
      }
      if (writeReviewStep) {
        contentSteps.push(
          { type: "select-review-type", required: false },
          writeReviewStep,
          { type: "video-review", required: false },
          { type: "choose-anonymity", required: true }
        );
        if (canSubmitEmploymentVerification) {
          contentSteps.push({ type: "verify-employment", required: false });
        }
      }
      const allSteps: StepConfig[] = [
        ...baseSteps,
        ...contentSteps,
        { type: "end", required: true },
      ];

      return {
        steps: allSteps,
        stepTypes: allSteps.map((step) => step.type),
        getStepConfig: (stepType: PostShiftReviewStep) =>
          allSteps.find((step) => step.type === stepType),
      };
    }

    // Old flow: pick-rating → [write-review + tag questions] → end
    const baseSteps: StepConfig[] = [{ type: "pick-rating", required: true }];
    const contentSteps: StepConfig[] = [];
    if (writeReviewStep) {
      contentSteps.push(writeReviewStep);
    }
    contentSteps.push(...questionSteps);
    const allSteps: StepConfig[] = [...baseSteps, ...contentSteps, { type: "end", required: true }];

    return {
      steps: allSteps,
      stepTypes: allSteps.map((step) => step.type),
      getStepConfig: (stepType: PostShiftReviewStep) =>
        allSteps.find((step) => step.type === stepType),
    };
  }, [
    questionMappings,
    isError,
    isWriteReviewUiUpdateEnabled,
    hasComparisonPair,
    canSubmitEmploymentVerification,
    hasPeerComments,
    source,
  ]);
}
